import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth, DEV_BYPASS_ENABLED } from "@/lib/auth-context";
import NoderaMark from "@/components/NoderaMark";
import { Shield, Sparkles, ArrowRight, Mail, Loader2, KeyRound } from "lucide-react";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { user, setSession } = useAuth();
  const navigate = useNavigate();

  // Already-authenticated admin → go straight to dashboard
  if (user && user.role === "admin") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <h1 className="font-display text-2xl font-bold text-slate-900">Already signed in.</h1>
          <Link
            to="/admin/dashboard"
            data-testid="admin-already-signed-in-link"
            className="mt-4 inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-5 py-2.5 text-sm font-semibold"
          >
            Go to dashboard <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  const sendMagicLink = async (e) => {
    e?.preventDefault?.();
    if (!email) {
      toast.error("Enter your admin email.");
      return;
    }
    try {
      setSubmitting(true);
      await api.post("/auth/magic-link/request", {
        email,
        role: "admin",
        redirect_path: "/admin/dashboard",
      });
      setLinkSent(true);
      toast.success("Check your email — sign-in link sent.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Email and password are required.");
      return;
    }
    try {
      setSubmitting(true);
      const r = await api.post("/auth/admin/login", { email, password });
      setSession(r.data.token, r.data.user);
      toast.success("Admin session active.");
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const devBypass = async () => {
    try {
      setSubmitting(true);
      const r = await api.post("/auth/dev/admin-bypass");
      setSession(r.data.token, r.data.user);
      toast.success("Dev admin session active.");
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-white relative overflow-hidden flex flex-col"
    >
      {/* Same ambient brand blobs as the Coming Soon page so the visual */}
      {/* language is consistent across all public-facing surfaces. */}
      <span className="hero-blob bg-blue-200 top-[-160px] left-[-160px] w-[420px] h-[420px]" />
      <span className="hero-blob bg-indigo-100 bottom-[-160px] right-[-160px] w-[420px] h-[420px] opacity-60" />

      {/* Minimal brand bar — no marketing nav. */}
      <header className="relative z-10 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link
          to="/"
          data-testid="admin-login-brand"
          className="flex items-center gap-2.5 group"
        >
          <span className="inline-flex w-7 h-7 items-center justify-center text-[#1F4853]">
            <NoderaMark size={24} />
          </span>
          <span className="font-display font-bold text-slate-900 text-base tracking-tight">
            Nodera <span className="text-slate-400 font-medium">Health</span>
          </span>
        </Link>
        <div className="text-xs text-slate-400 font-medium uppercase tracking-[0.18em]">
          Internal access
        </div>
      </header>

      <div className="relative z-10 flex-1 flex items-center justify-center px-4 py-10">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <span className="inline-flex w-12 h-12 rounded-xl bg-slate-900 text-white items-center justify-center shadow-sm">
              <Shield className="w-5 h-5" />
            </span>
            <h1 className="mt-5 font-display text-3xl font-extrabold tracking-tighter text-slate-900">
              Nodera admin
            </h1>
            <p className="mt-2 text-sm text-slate-600">Internal access only.</p>
          </div>

        {linkSent ? (
          <div
            data-testid="admin-login-magic-link-sent"
            className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-6 space-y-3 shadow-sm"
          >
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-700">
                <Mail className="w-5 h-5" />
              </span>
              <div>
                <div className="font-display font-bold text-slate-900">Check your inbox</div>
                <div className="text-sm text-slate-600">We sent a sign-in link to <b>{email}</b>.</div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setLinkSent(false)}
              className="text-xs font-semibold text-slate-700 hover:text-slate-900"
            >
              ← Use a different email
            </button>
          </div>
        ) : (
          <form
            onSubmit={sendMagicLink}
            data-testid="admin-login-form"
            className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4 shadow-sm"
          >
            <Field label="Admin email">
              <input
                type="email"
                required
                data-testid="admin-login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
                autoComplete="username"
              />
            </Field>
            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-magic-link-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 text-white rounded-full px-5 py-3 text-sm font-semibold hover:bg-slate-800 disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Sending link…
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4" /> Email me a sign-in link
                </>
              )}
            </button>

            <div className="pt-3 border-t border-slate-100">
              {showPassword ? (
                <div className="space-y-3">
                  <Field label="Password">
                    <input
                      type="password"
                      required
                      data-testid="admin-login-password-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          submit(e);
                        }
                      }}
                      className={inputCls}
                      autoComplete="current-password"
                    />
                  </Field>
                  <button
                    type="button"
                    onClick={submit}
                    disabled={submitting}
                    data-testid="admin-login-submit-btn"
                    className="w-full inline-flex items-center justify-center gap-2 bg-white border border-slate-200 text-slate-900 rounded-full px-5 py-3 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
                  >
                    <KeyRound className="w-4 h-4" /> Log in with password
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowPassword(true)}
                  data-testid="admin-login-show-password-btn"
                  className="block w-full text-center text-xs font-semibold text-slate-500 hover:text-slate-900"
                >
                  Or sign in with a password
                </button>
              )}
            </div>

            {DEV_BYPASS_ENABLED && (
              <div className="pt-3 border-t border-slate-100">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-amber-700">
                  <Sparkles className="w-3.5 h-3.5" /> Dev environment
                </div>
                <button
                  type="button"
                  onClick={devBypass}
                  disabled={submitting}
                  data-testid="admin-login-dev-bypass-btn"
                  className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-amber-50 text-amber-900 border border-amber-200 rounded-full px-5 py-3 text-sm font-semibold hover:bg-amber-100"
                >
                  Skip auth (dev admin)
                </button>
              </div>
            )}
          </form>
        )}

        <p className="mt-6 text-center text-xs text-slate-500">
          Not an admin? <Link to="/login" className="underline hover:text-slate-900">Clinic portal login</Link>
        </p>
        </div>
      </div>

      <footer className="relative z-10 border-t border-slate-100 bg-white/60 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 text-xs text-slate-500 flex items-center justify-between">
          <div>© {new Date().getFullYear()} Nodera Health</div>
          <div className="font-medium">Operational infrastructure only · No clinical care</div>
        </div>
      </footer>
    </div>
  );
}

const inputCls =
  "w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-700 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
